import React, { useEffect } from 'react';
import { Form, Input, Select, Typography, Button, Space, Card } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';

const { TextArea } = Input;
const { Text } = Typography;
const { Option } = Select;

const CalculationNodeProperties = ({ config, nodeType, inputSchema, updateConfig }) => {
  const [form] = Form.useForm();

  // Sync form with config when it changes
  useEffect(() => {
    if (nodeType === 'sort') {
      // Normalize to sortColumns array format
      let sortColumns = config.sortColumns || [];
      if (sortColumns.length === 0 && config.column) {
        sortColumns = [{ column: config.column, direction: config.direction || 'asc' }];
      }
      if (sortColumns.length === 0) {
        sortColumns = [{ column: undefined, direction: 'asc' }];
      }
      form.setFieldsValue({ sortColumns });
    } else {
      form.setFieldsValue(config);
    }
  }, [config, form, nodeType]);

  const handleValuesChange = (changedValues, allValues) => {
    if (updateConfig) {
      if (nodeType === 'sort') {
        // Also set legacy format for backward compatibility
        const sortColumns = allValues.sortColumns || [];
        const firstSort = sortColumns[0] || {};
        updateConfig({
          config: {
            ...allValues,
            column: firstSort.column,
            direction: firstSort.direction || 'asc'
          }
        });
      } else {
        updateConfig({ config: allValues });
      }
    }
  };

  if (nodeType === 'calculate_field') {
    return (
      <Form form={form} layout="vertical" onValuesChange={handleValuesChange}>
        <Form.Item label="New Field Name" name="newField" initialValue={config.newField} rules={[{ required: true }]}>
          <Input placeholder="new_column_name" />
        </Form.Item>
        <Form.Item label="Formula" name="formula" initialValue={config.formula} rules={[{ required: true }]}>
          <TextArea 
            rows={3} 
            placeholder="e.g., clicks / impressions * 100"
          />
        </Form.Item>
        <Text type="secondary" style={{ fontSize: '11px' }}>
          💡 Use column names in formula. Supported operators: +, -, *, /, (), functions: sum, avg, min, max
        </Text>
        {inputSchema && inputSchema.length > 0 && (
          <div style={{ marginTop: 8 }}>
            <Text type="secondary" style={{ fontSize: '11px' }}>Available Columns:</Text>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px' }}>
              {inputSchema.map(col => (
                <span key={col.name} style={{ 
                  fontSize: '10px', 
                  background: '#f0f0f0', 
                  padding: '2px 6px', 
                  borderRadius: '4px',
                  border: '1px solid #d9d9d9'
                }}>
                  {col.name}
                </span>
              ))}
            </div>
          </div>
        )}
      </Form>
    );
  }

  if (nodeType === 'sort') {
    return (
      <Form form={form} layout="vertical" onValuesChange={handleValuesChange}>
        <div style={{ marginBottom: 12, padding: '8px 12px', background: '#f0f5ff', borderRadius: 4, border: '1px solid #adc6ff' }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Sort data by one or more columns. Rows will be sorted in the order of columns listed.
          </Text>
        </div>

        <Form.List name="sortColumns">
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }, index) => (
                <Card 
                  key={key} 
                  size="small" 
                  style={{ marginBottom: 8, backgroundColor: '#fafafa' }}
                  bodyStyle={{ padding: '8px 12px' }}
                >
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <Text type="secondary" style={{ minWidth: 20 }}>#{index + 1}</Text>
                    <Form.Item
                      {...restField}
                      name={[name, 'column']}
                      rules={[{ required: true, message: 'Select column' }]}
                      style={{ marginBottom: 0, flex: 2 }}
                    >
                      {inputSchema && inputSchema.length > 0 ? (
                        <Select placeholder="Select column" showSearch>
                          {inputSchema.map(col => (
                            <Option key={col.name} value={col.name}>{col.name}</Option>
                          ))}
                        </Select>
                      ) : (
                        <Input placeholder="Column name" />
                      )}
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, 'direction']}
                      initialValue="asc"
                      style={{ marginBottom: 0, flex: 1 }}
                    >
                      <Select>
                        <Option value="asc">↑ Ascending</Option>
                        <Option value="desc">↓ Descending</Option>
                      </Select>
                    </Form.Item>
                    {fields.length > 1 && (
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => remove(name)}
                      />
                    )}
                  </div>
                </Card>
              ))}
              <Form.Item style={{ marginTop: 8 }}>
                <Button type="dashed" onClick={() => add({ direction: 'asc' })} block icon={<PlusOutlined />}>
                  Add Sort Column
                </Button>
              </Form.Item>
            </>
          )}
        </Form.List>
      </Form>
    );
  }

  if (nodeType === 'group_by') {
    return (
      <Form form={form} layout="vertical" onValuesChange={handleValuesChange}>
        <Form.Item label="Group By Columns" name="groupBy" initialValue={config.groupBy || []}>
          <Select mode="tags" placeholder="Select columns to group by">
            {inputSchema && inputSchema.map(col => (
              <Option key={col.name} value={col.name}>{col.name}</Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item label="Aggregations" name="aggregations" initialValue={config.aggregations || []}>
          <div>
            {/* This would be a dynamic list for aggregation functions */}
            <Text type="secondary">Aggregation functions will be added here</Text>
          </div>
        </Form.Item>
      </Form>
    );
  }

  return null;
};

export default CalculationNodeProperties;